import { format, parseISO } from 'date-fns'

import { EMPTY_LIST } from '../../../../../constants/common'
import { DATE_FULL_FORMAT_FROM_TIME } from '../../../../../constants/date'
import { toUkTimezoneDate } from '../../../../../utils/date'

import {
  APPLICANT_DECISION_ELIGIBLE_COUNT_FALLBACK,
  APPLICANT_DECISION_NO_REVIEWERS_COUNT,
  APPLICANT_DECISION_STATUS_TEXT_COLOR,
} from './ApplicantDecisionPanel.constants'
import {
  getApplicantDecisionEligibleCount,
  getIsApplicantDecisionAwaiting,
  getIsApplicantFourEyesRequired,
  getIsApplicantSelectionRejected,
} from './ApplicantDecisionPanel.utils'

function getIsApplicantDecisionReviewerless(applicantData) {
  return (
    getApplicantDecisionEligibleCount(applicantData) ===
    APPLICANT_DECISION_NO_REVIEWERS_COUNT
  )
}

function getApplicantDecisionTimeText({ timezone, value }) {
  return value
    ? format(
        toUkTimezoneDate(parseISO(value), timezone),
        DATE_FULL_FORMAT_FROM_TIME
      )
    : null
}

function toApplicantDecisionSessionValue(currentUser) {
  return currentUser
    ? {
        avatar: currentUser.avatar,
        avatarColor: currentUser.avatar_color,
        email: currentUser.email,
        fullname: currentUser.fullname,
        id: currentUser.id,
      }
    : null
}

function toApplicantDecisionUserValue(user) {
  return user
    ? {
        avatarColor: user.avatar_color,
        companyPostName: user.job_title,
        email: user.email,
        fullname: user.full_name,
        id: user.id,
      }
    : null
}

function getApplicantDecisionReviewers(applicantData) {
  return applicantData.eligible_reviewers ?? EMPTY_LIST
}

export function getApplicantDecisionAwaitingText({ applicantData, t }) {
  return getIsApplicantDecisionAwaiting(applicantData) &&
    getIsApplicantFourEyesRequired(applicantData)
    ? t('RentalPortal.ApplicantDecisionAwaitingReviewerKey', {
        count:
          getApplicantDecisionEligibleCount(applicantData) ??
          APPLICANT_DECISION_ELIGIBLE_COUNT_FALLBACK,
      })
    : null
}

export function getApplicantDecisionEscalationKey(applicantData) {
  return getIsApplicantDecisionAwaiting(applicantData) &&
    getIsApplicantFourEyesRequired(applicantData) &&
    getIsApplicantDecisionReviewerless(applicantData)
    ? 'RentalPortal.ApplicantDecisionEscalationHintKey'
    : null
}

export function getApplicantDecisionSelectedPerson({
  applicantData,
  currentUser,
  t,
  timezone,
}) {
  if (applicantData?.is_selected && applicantData.selected_by) {
    return {
      note: applicantData.selection_note
        ? {
            label: t('RentalPortal.ApplicantDecisionSelectionNoteLabelKey'),
            text: applicantData.selection_note,
          }
        : null,
      statusProps: {
        text: t('RentalPortal.ApplicantDecisionStatusSelectedKey'),
        textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.selected,
      },
      text: getApplicantDecisionTimeText({
        timezone,
        value: applicantData.selected_at,
      }),
      user: toApplicantDecisionUserValue(applicantData.selected_by),
    }
  }

  return applicantData?.permissions?.can_select && currentUser
    ? {
        isMuted: true,
        text: t('RentalPortal.ApplicantDecisionSelectedByProspectiveKey'),
        user: toApplicantDecisionSessionValue(currentUser),
      }
    : {
        isMuted: true,
        text: t('RentalPortal.ApplicantDecisionSelectedByEmptyKey'),
      }
}

export function getApplicantDecisionApprovalsStep({ applicantData, t }) {
  return {
    hint: getIsApplicantFourEyesRequired(applicantData)
      ? t('RentalPortal.ApplicantDecisionFourEyesRuleKey', {
          count:
            getApplicantDecisionEligibleCount(applicantData) ??
            APPLICANT_DECISION_ELIGIBLE_COUNT_FALLBACK,
        })
      : t('RentalPortal.ApplicantDecisionNoSecondApproverKey'),
    label: t('RentalPortal.ApplicantDecisionApprovalsLabelKey'),
  }
}

export function getApplicantDecisionApprovalPeople({
  applicantData,
  t,
  timezone,
}) {
  if (applicantData?.approved_by) {
    return [
      {
        id: applicantData.approved_by.id,
        note: applicantData.approval_note
          ? {
              label: t('RentalPortal.ApplicantDecisionReviewerNoteLabelKey'),
              text: applicantData.approval_note,
            }
          : null,
        statusProps: {
          text: t('RentalPortal.ApplicantDecisionStatusApprovedKey'),
          textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.approved,
        },
        text: getApplicantDecisionTimeText({
          timezone,
          value: applicantData.approved_at,
        }),
        user: toApplicantDecisionUserValue(applicantData.approved_by),
      },
    ]
  }

  if (getIsApplicantSelectionRejected(applicantData)) {
    return [
      {
        id: applicantData.rejected_by?.id,
        note: applicantData.rejection_reason
          ? {
              label: t('RentalPortal.ApplicantDecisionRejectReasonLabelKey'),
              text: applicantData.rejection_reason,
            }
          : null,
        statusProps: {
          text: t('RentalPortal.ApplicantDecisionStatusRejectedKey'),
          textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.rejected,
        },
        text: getApplicantDecisionTimeText({
          timezone,
          value: applicantData.rejected_at,
        }),
        user: toApplicantDecisionUserValue(applicantData.rejected_by),
      },
    ]
  }

  if (!getIsApplicantDecisionAwaiting(applicantData)) {
    return EMPTY_LIST
  }

  const awaitingStatusProps = {
    text: t('RentalPortal.ApplicantDecisionStatusAwaitingKey'),
    textColor: APPLICANT_DECISION_STATUS_TEXT_COLOR.awaiting,
  }

  return getIsApplicantFourEyesRequired(applicantData)
    ? getApplicantDecisionReviewers(applicantData).map(reviewer => ({
        id: reviewer.id,
        isMuted: true,
        statusProps: awaitingStatusProps,
        user: toApplicantDecisionUserValue(reviewer),
      }))
    : [
        {
          id: applicantData.selected_by?.id,
          isMuted: true,
          statusProps: awaitingStatusProps,
          user: toApplicantDecisionUserValue(applicantData.selected_by),
        },
      ]
}
